import React, { type ReactElement } from 'react';
import {
  ArrowCounterClockwise,
  ChatCircle,
  Check,
  FileText,
  LinkSimple,
  PencilSimpleLine,
  Trash,
  VideoCamera,
  X,
} from '@phosphor-icons/react';
import { campaignCopy, type RosterRowRef } from '@discovr/features';
import { Button } from '../../../components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../components/ui/tooltip';
import { isRejectedStatus, reinstateNeedsAdminLevel, resolvePreSelectedDeliverable } from './helpers';
import type { CreatorsRow, CreatorsSubTab } from './types';
import type { QuickActionStage } from '../../utils/campaignPipeline';
import { profileOfferability, profileOfferabilityAtPending, type ProfileOfferability } from './creatorLookupStatus';

const OFFERABLE: ProfileOfferability = { offerable: true };

/** A disabled native <button> is unhoverable (pointer-events: none) and
 *  unfocusable, so Radix's hover/focus-triggered tooltip never opens on it.
 *  Wrap it in a focusable, hoverable span and make THAT the trigger (the
 *  Radix-documented pattern) — but only while disabled, or an enabled
 *  button would carry two tab stops. */
function DisabledTooltipTrigger({ disabled, children }: { disabled: boolean; children: ReactElement }) {
  return (
    <TooltipTrigger asChild>
      {disabled ? (
        <span tabIndex={0} className="inline-flex rounded-full focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring">
          {children}
        </span>
      ) : (
        children
      )}
    </TooltipTrigger>
  );
}

interface CreatorRowActionsProps {
  c: CreatorsRow;
  /**
   * Which column this instance renders. `actions` is the shared Approve · Chat ·
   * Reject cluster (both brand + admin). `quick` holds the everyday admin
   * write-access fast path — Approve as Brand · Add Script — kept out of
   * Manage since neither is an elevated/destructive action the way Remove is.
   * `manage` holds the remaining row-management actions — Edit · Remove ·
   * Reinstate — gated on capability, not a role tier; a Staff (admin_member)
   * viewer with a write+ level grant sees it too.
   */
  column: 'actions' | 'quick' | 'manage';
  isAdminViewer: boolean;
  creatorsSubTab: CreatorsSubTab;
  permissions: {
    canOpenConversation?: boolean;
    canApproveRejectCreators?: boolean;
    canApproveAcceptedCreators?: boolean;
    canApproveOnBehalfOfBrand?: boolean;
    canRemoveOnPending?: boolean;
    canReinstateRejected?: boolean;
    canMoveToScriptReview?: boolean;
    /** Gates the Edit-creator pencil — its own dedicated capability, distinct
     *  from `canApproveOnBehalfOfBrand`: editing a creator's profile and
     *  approving them on the brand's behalf are unrelated actions. */
    canEditCreator?: boolean;
  };
  setConvCreator?: ((c: RosterRowRef) => void) | null;
  onEditCreator?: ((c: Record<string, unknown>) => void) | null;
  onConfirmReject: (creator: CreatorsRow) => void;
  onConfirmDelete: (creator: CreatorsRow) => void;
  onConfirmApproveAsBrand: (creator: CreatorsRow, preSelectedTypes: string[]) => void;
  onConfirmReinstate: (creator: CreatorsRow) => void;
  onConfirmMoveToScriptReview: (creator: CreatorsRow, preSelectedTypes: string[]) => void;
  onConfirmBrandApprove?: (creator: CreatorsRow, preSelectedTypes: string[]) => void;
  onConfirmAdminApprove?: (creator: CreatorsRow, preSelectedTypes: string[]) => void;
  optimisticSelectedTypes?: string[] | null;
  /** First stage the campaign runs; picks the quick action (default: script). */
  quickStage?: QuickActionStage;
  /** Null hides Roll back (Pending); the caller gates lane, public view and permission. */
  onRollbackToPending?: ((creator: CreatorsRow, opener: HTMLElement | null) => void) | null;
  rollbackToPendingBlockedReason?: string | null;
}

const ROLLBACK_TO_PENDING_LABEL = campaignCopy.creator.rollback.brandApprovalStage.menu;

const QUICK_STAGE_UI: Record<QuickActionStage, { label: string; hint: string; icon: ReactElement }> = {
  script: { label: 'Add Script', hint: 'Move straight to Script Review', icon: <FileText size={12} /> },
  content: { label: 'Add Content', hint: 'Approve and submit content for review', icon: <VideoCamera size={12} /> },
  live: { label: 'Add Live URL', hint: 'Approve and put this creator live', icon: <LinkSimple size={12} /> },
};

export function CreatorRowActions({
  c,
  column,
  isAdminViewer,
  creatorsSubTab,
  permissions,
  setConvCreator,
  onEditCreator,
  onConfirmReject,
  onConfirmDelete,
  onConfirmApproveAsBrand,
  onConfirmReinstate,
  onConfirmMoveToScriptReview,
  onConfirmBrandApprove,
  onConfirmAdminApprove,
  optimisticSelectedTypes,
  quickStage = 'script',
  onRollbackToPending,
  rollbackToPendingBlockedReason,
}: CreatorRowActionsProps) {
  const creatorId = c.id;
  const status = c.status;
  const isPending = status === 'pending';
  const isAcceptedByBrand = status === 'accepted_by_brand';
  // Rejected rows have no selection_thread_locked
  // field on the unified shape — the rejected
  // status itself is the "locked" signal there.
  const isLocked =
    ('selection_thread_locked' in c ? c.selection_thread_locked : false) ||
    isRejectedStatus(status);

  // BE-hydrated unread badge for the selection thread.
  const unread = 'unread_count' in c ? c.unread_count : 0;
  const persistedSelectedTypes = c.deliverables.filter((d) => d.is_selected).map((d) => d.type);
  const latestSelectedTypes =
    optimisticSelectedTypes !== undefined ? (optimisticSelectedTypes ?? []) : persistedSelectedTypes;

  // Chat — always clickable when the user has the
  // permission. Locked threads still open so
  // brand / admin can read prior messages
  // (history is useful after rejection); the
  // composer inside ChatThread disables itself
  // when locked so no new messages can land.
  const chatBtn =
    permissions.canOpenConversation && setConvCreator ? (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            type="button"
            onClick={() => setConvCreator({ ...c, id: creatorId } as unknown as RosterRowRef)}
            aria-label={isLocked ? 'View conversation (read-only)' : 'Open conversation'}
            className={
              isLocked
                ? 'relative size-9 rounded-full border border-border bg-muted text-muted-foreground hover:bg-muted'
                : 'relative size-9 rounded-full border border-border text-muted-foreground hover:bg-muted'
            }
          >
            <ChatCircle size={16} />
            {unread > 0 && (
              // Fixed height so it's a circle at 1 digit and grows only in width
              // for bigger counts (never taller). Matches UnreadBadge behaviour.
              <span className="absolute -top-1 -right-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-violet-600 px-1 text-center text-[9px] font-black leading-none tabular-nums text-white">
                {unread > 99 ? '99+' : unread}
              </span>
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          {isLocked ? 'View conversation (read-only)' : 'Open conversation'}
        </TooltipContent>
      </Tooltip>
    ) : null;

  // Edit (drawer) — gated on canEditCreator (write+ level grant), not a role
  // tier; active on pending, grayed lock-icon on anything else. Uses the DS
  // outline variant directly rather than ghost + a manual border override.
  const editBtn =
    permissions.canEditCreator && onEditCreator ? (
      isPending ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              type="button"
              onClick={() => onEditCreator(c as unknown as Record<string, unknown>)}
              aria-label="Edit creator data"
              className="rounded-full text-muted-foreground hover:text-foreground"
            >
              <PencilSimpleLine size={16} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Edit creator data</TooltipContent>
        </Tooltip>
      ) : (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              type="button"
              disabled
              aria-label="Edit locked"
              className="rounded-full bg-muted text-muted-foreground"
            >
              <PencilSimpleLine size={16} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Edit locked</TooltipContent>
        </Tooltip>
      )
    ) : null;

  // Approve-as-Brand pill — admin only, pending only.
  // Pre-fill the dialog's deliverable choice from the
  // row's commercial-popover draft (client-side only,
  // never persisted to BE) so admin doesn't have to
  // pick twice.
  // The BE refuses to approve a pending creator whose profile isn't public
  // and fetched (409) — for the brand and for an admin on their behalf.
  // Disable the control with the reason rather than let the click fail.
  // Only the quick column renders the gated controls, so only it pays for
  // the check. No aria-label: Radix wires the reason via aria-describedby.
  const offerability = column === 'quick' ? profileOfferability(c) : OFFERABLE;
  const approveAsBrandBtn =
    isAdminViewer && isPending && permissions.canApproveOnBehalfOfBrand ? (
      <Tooltip>
        <DisabledTooltipTrigger disabled={!offerability.offerable}>
          <Button
            type="button"
            disabled={!offerability.offerable}
            onClick={() => {
              // Pass the full selection (main + addons) to the confirmation
              // dialog. Fall back to resolvePreSelectedDeliverable (main only)
              // when nothing has been optimistically or persistently selected.
              const types =
                latestSelectedTypes.length > 0
                  ? latestSelectedTypes
                  : (() => {
                      const fallback = resolvePreSelectedDeliverable(c, null);
                      return fallback ? [fallback] : [];
                    })();
              onConfirmApproveAsBrand(c, types);
            }}
            className="h-9 gap-1.5 rounded-full border border-indigo-200 bg-indigo-50 px-3 text-[11px] font-extrabold tracking-wide text-indigo-700 uppercase hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Check size={12} />
            As Brand
          </Button>
        </DisabledTooltipTrigger>
        <TooltipContent>{offerability.offerable ? 'Approve on behalf of brand' : offerability.reason}</TooltipContent>
      </Tooltip>
    ) : null;

  // Approve-by-Admin pill — admin only, on accepted_by_brand rows
  // inside the Approved sub-tab. Opens the deliverable picker (ApprovalCard)
  // pre-filled with the brand's selection so admin can review and confirm.
  const approveAsAdminBtn =
    isAdminViewer &&
    isAcceptedByBrand &&
    creatorsSubTab === 'approved' &&
    permissions.canApproveRejectCreators ? (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            type="button"
            aria-label="Approve as admin"
            onClick={() => {
              const types =
                latestSelectedTypes.length > 0
                  ? latestSelectedTypes
                  : (() => {
                      const fallback = resolvePreSelectedDeliverable(c, null);
                      return fallback ? [fallback] : [];
                    })();
              onConfirmAdminApprove?.(c, types);
            }}
            className="size-9 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
          >
            <Check size={16} />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Approve as admin</TooltipContent>
      </Tooltip>
    ) : null;

  // Brand Approve — pending rows only, brand viewer.
  // Opens the deliverable picker (ApprovalCard) so brand can select
  // main + add-ons before confirming, matching the admin "As Brand" flow.
  const brandApproveBtn =
    !isAdminViewer && isPending && permissions.canApproveRejectCreators ? (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            type="button"
            aria-label="Approve"
            // Same marker the commercial trigger / View-analysis button already
            // carry: without it, PublicCampaignShell's click-capture cancels
            // the click before onConfirmBrandApprove (which captures the
            // resumable approve_creator action) ever runs.
            data-public-allowed=""
            onClick={() => {
              const types =
                latestSelectedTypes.length > 0
                  ? latestSelectedTypes
                  : (() => {
                      const fallback = resolvePreSelectedDeliverable(c, null);
                      return fallback ? [fallback] : [];
                    })();
              onConfirmBrandApprove?.(c, types);
            }}
            className="size-9 rounded-full border border-border text-emerald-600 hover:bg-emerald-50"
          >
            <Check size={16} />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Approve</TooltipContent>
      </Tooltip>
    ) : null;

  // Reject — brand: pending only; admin: accepted_by_brand only.
  // (Admin pending rows show Trash/Remove, not Reject.)
  const showRejectForBrand = !isAdminViewer && isPending;
  const showRejectForAdmin = isAdminViewer && isAcceptedByBrand;
  const rejectBtn =
    permissions.canApproveRejectCreators && (showRejectForBrand || showRejectForAdmin) ? (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            type="button"
            aria-label="Reject"
            data-public-allowed=""
            onClick={() => onConfirmReject(c)}
            className="size-9 rounded-full border border-border text-rose-600 hover:bg-rose-50"
          >
            <X size={16} />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Reject</TooltipContent>
      </Tooltip>
    ) : null;

  // Delete — admin only, pending only. Hard-deletes the entry outright; it
  // does NOT become a terminal rejected row, so nothing lands on the
  // Rejected tab and there is nothing to reinstate afterwards.
  const deleteBtn =
    isAdminViewer && isPending && permissions.canRemoveOnPending ? (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            type="button"
            aria-label="Delete creator"
            onClick={() => onConfirmDelete(c)}
            className="size-9 rounded-full border border-red-200 text-red-600 hover:bg-red-50"
          >
            <Trash size={16} />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Delete permanently — this cannot be undone</TooltipContent>
      </Tooltip>
    ) : null;

  // Reinstate — admin only, on any terminal row (the three rejected_*
  // statuses plus legacy removed_by_admin rows — see isRejectedStatus). Sends
  // the creator back to pending; the inverse of a completed rejection. Note
  // deleteBtn has no inverse: a deleted entry is gone, not terminal.
  const canReinstateThis =
    permissions.canReinstateRejected && (!reinstateNeedsAdminLevel(status) || permissions.canRemoveOnPending);
  const reinstateBtn =
    isAdminViewer && isRejectedStatus(status) && canReinstateThis ? (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            type="button"
            onClick={() => onConfirmReinstate(c)}
            aria-label="Send back to Pending"
            className="rounded-full border-indigo-200 text-indigo-700 hover:bg-indigo-50"
          >
            <ArrowCounterClockwise size={16} />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Send back to Pending</TooltipContent>
      </Tooltip>
    ) : null;

  const showRollbackToPending = isAdminViewer && isAcceptedByBrand && creatorsSubTab === 'approved' && onRollbackToPending;
  const atPending = showRollbackToPending ? profileOfferabilityAtPending(c) : OFFERABLE;
  const rollbackReason = rollbackToPendingBlockedReason ?? (atPending.offerable ? null : atPending.reason);
  const rollbackBlocked = !!rollbackReason;
  const rollbackToPendingBtn =
    showRollbackToPending ? (
      <Tooltip>
        <DisabledTooltipTrigger disabled={rollbackBlocked}>
          <Button
            variant="outline"
            size="icon"
            type="button"
            disabled={rollbackBlocked}
            aria-label={ROLLBACK_TO_PENDING_LABEL}
            onClick={(event) => onRollbackToPending(c, event.currentTarget)}
            className="rounded-full border-indigo-200 text-indigo-700 hover:bg-indigo-50"
          >
            <ArrowCounterClockwise size={16} />
          </Button>
        </DisabledTooltipTrigger>
        <TooltipContent>{rollbackReason ?? ROLLBACK_TO_PENDING_LABEL}</TooltipContent>
      </Tooltip>
    ) : null;

  // Add Script — visible to any admin-side user with write+ campaign access,
  // pending rows only. Clickable once a deliverable is selected (same
  // latestSelectedTypes state approveAsBrandBtn reads); the confirm dialog
  // additionally requires a non-empty script before its own Confirm enables
  // (see ApprovalCard's canConfirm). isAdminViewer is defense-in-depth here —
  // canMoveToScriptReview is already false for every brand role.
  const addScriptDisabled = latestSelectedTypes.length === 0 || !offerability.offerable;
  const addScriptBtn =
    isAdminViewer && isPending && permissions.canMoveToScriptReview ? (
      <Tooltip>
        <DisabledTooltipTrigger disabled={addScriptDisabled}>
          <Button
            type="button"
            disabled={addScriptDisabled}
            onClick={() => onConfirmMoveToScriptReview(c, latestSelectedTypes)}
            className="h-9 gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 text-[11px] font-extrabold tracking-wide text-emerald-700 uppercase hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {QUICK_STAGE_UI[quickStage].icon}
            {QUICK_STAGE_UI[quickStage].label}
          </Button>
        </DisabledTooltipTrigger>
        <TooltipContent>
          {!offerability.offerable
            ? offerability.reason // approves as brand first, so the same gate applies
            : latestSelectedTypes.length === 0
              ? 'Select a deliverable first'
              : QUICK_STAGE_UI[quickStage].hint}
        </TooltipContent>
      </Tooltip>
    ) : null;

  // Quick Actions column — Approve as Brand · Add Script, sits between
  // Actions and Manage. Kept out of Manage since neither is an
  // elevated/destructive action — just the ordinary write-access fast path.
  if (column === 'quick') {
    return (
      <div className="flex items-center justify-center gap-2">
        {approveAsBrandBtn}
        {addScriptBtn}
      </div>
    );
  }

  // Manage column — gated on capability (canRemoveOnPending / canEditCreator /
  // canReinstateRejected), not a role tier. Row-management actions grouped
  // away from the everyday Approve / Chat / Reject cluster.
  if (column === 'manage') {
    return (
      <div className="flex items-center justify-center gap-2">
        {editBtn}
        {deleteBtn}
        {reinstateBtn}
        {rollbackToPendingBtn}
      </div>
    );
  }

  // Actions column — Approve (left) · Chat (center) · Reject (right).
  // The everyday approval sits on the left: "Approve as Admin" for the admin
  // viewer, the icon Approve for the brand viewer. Chat is the middle button.
  const approveBtn = isAdminViewer ? approveAsAdminBtn : brandApproveBtn;
  return (
    <div className="flex items-center justify-center gap-2">
      {approveBtn}
      {chatBtn}
      {rejectBtn}
    </div>
  );
}
